import React, { useState, useEffect, useMemo, useRef } from "react";
import { ChevronDown, AlertCircle, Copy } from "lucide-react";
import {
  invokeLambda,
  getAvailableLambdaFunctions,
  getPatientLambdaData,
} from "./api_utils";
import AudioLookup from "./AudioLookup";
import ConversationIdFinder from "../shared/ConversationIdFinder";
import LambdaLogPanel from "../shared/LambdaLogPanel";
import LambdaRunBadge from "../shared/LambdaRunBadge";
import { useLambdaRuns } from "../shared/useLambdaRun";
import { buildMergeAudioChunksEventJson } from "../../utils/conversationIds";
import {
  createSummarizeJobName,
  createTranscribeJobName,
  environment,
} from "../../config/environment";

const LambdaInvokerDashboard = () => {
  const [selectedFunction, setSelectedFunction] = useState("");
  const [patientId, setPatientId] = useState("");
  const [platform, setPlatform] = useState("");
  const [eventJson, setEventJson] = useState("");
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingPatientData, setLoadingPatientData] = useState(false);
  const [error, setError] = useState(null);
  const [jsonValid, setJsonValid] = useState(true);
  const [availableFunctions, setAvailableFunctions] = useState([]);
  // One entry, but the plural hook keeps the polling logic in one place.
  const invokedRuns = useMemo(() => {
    if (!response?.requestId || !response?.functionName) return [];
    return [
      {
        functionKey: response.functionName,
        functionName: response.functionName,
        requestId: response.requestId,
        startedAt: response.timestamp
          ? Date.parse(response.timestamp)
          : undefined,
        initialStatus: response.status,
        initialLogs: response.tailLogs,
      },
    ];
  }, [response]);

  const {
    runs: trackedRuns,
    status: runStatus,
    durationMs: runDurationMs,
    refresh: refreshRunLogs,
    refreshing: refreshingRunLogs,
  } = useLambdaRuns(invokedRuns);
  const trackedRun = trackedRuns[0] || null;

  const isUploadConversationFunction =
    selectedFunction === "UploadConversationFunction";
  const isRunTranscriptionFunction =
    selectedFunction === "RunTranscriptionFunction";
  const isMergeAudioChunksFunction =
    selectedFunction === "MergeAudioChunksFunction";
  const [selectedConversationId, setSelectedConversationId] = useState("");

  // Persists fetched transcription data so platform changes can rebuild without re-fetching
  const transcriptionDataRef = useRef({ convId: "", audioLength: "" });

  // Mobile path: zero-pad month and day segments of "Name/M/D/YYYY-..."
  const padMobilePath = (convId) => {
    const parts = convId.split("/");
    if (parts.length >= 3) {
      parts[1] = parts[1].padStart(2, "0");
      parts[2] = parts[2].padStart(2, "0");
    }
    return parts.join("/");
  };

  // Webapp path: strip leading zeros from month and day segments
  const unpadWebPath = (convId) => {
    const parts = convId.split("/");
    if (parts.length >= 3) {
      parts[1] = String(parseInt(parts[1], 10));
      parts[2] = String(parseInt(parts[2], 10));
    }
    return parts.join("/");
  };

  const buildTranscriptionBody = (convId, selectedPlatform, audioLength) => {
    const isMobile = selectedPlatform === "mobile";
    const objectPath = isMobile ? padMobilePath(convId) : unpadWebPath(convId);
    const body = {
      bucket_name: isMobile
        ? environment.mobileAudioBucket
        : environment.mergedAudioBucket,
      object_key: `${objectPath}.mp3`,
      conversation_id: unpadWebPath(convId),
      failed_models: [],
      audio_length_seconds: audioLength || "",
    };
    if (selectedPlatform) {
      body.platform = selectedPlatform;
    }
    return body;
  };

  const formatDateMDYYYY = (date = new Date()) => {
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const extractUsernameFromEmail = (email) => {
    if (!email || typeof email !== "string") return "";
    return email.split("@")[0] || "";
  };

  // Load available functions from server
  useEffect(() => {
    const loadFunctions = async () => {
      try {
        const functionsData = await getAvailableLambdaFunctions();

        // Handle both object and array formats
        let functionKeys;
        if (Array.isArray(functionsData)) {
          functionKeys = functionsData;
        } else if (
          typeof functionsData === "object" &&
          functionsData !== null
        ) {
          functionKeys = Object.keys(functionsData);
        } else {
          functionKeys = [];
        }

        setAvailableFunctions(functionKeys);
      } catch (error) {
        console.error("Failed to load Lambda functions:", error);
        console.error("Error details:", error.message);
        setError(`Failed to load available Lambda functions: ${error.message}`);
      }
    };

    loadFunctions();
  }, []);

  // Validate JSON as user types
  useEffect(() => {
    if (!eventJson.trim()) {
      setJsonValid(true);
      return;
    }

    try {
      JSON.parse(eventJson);
      setJsonValid(true);
    } catch (e) {
      setJsonValid(false);
    }
  }, [eventJson]);

  // Auto-populate event JSON when patient ID and eligible function are selected
  useEffect(() => {
    const autoPopulateEventJson = async () => {
      // Only auto-populate for specific functions
      const eligibleFunctions = [
        "RunMDMFunction",
        "RunMacrosFunction",
        "RunICD10CodingV2",
        "RunChatGPTSummarizeFunction",
        "RunUpdateChartHistoryFunction",
        "RunInteguruSchedulePull",
        "RunTranscriptionFunction",
      ];

      const trimmedPatientId = patientId.trim();

      if (
        !trimmedPatientId ||
        !selectedFunction ||
        !eligibleFunctions.includes(selectedFunction)
      ) {
        return;
      }

      setLoadingPatientData(true);
      setError(null);
      setEventJson("Loading patient data...");

      try {
        const data = await getPatientLambdaData(trimmedPatientId);
        console.log("data", data);

        const {
          patient,
          summarizeJob,
          transcribeJob,
          conversationId,
          totalChunks,
        } = data;

        let eventStructure = null;

        if (selectedFunction === "RunMDMFunction") {
          // Construct RunMDM event structure
          eventStructure = {
            Records: [
              {
                body: JSON.stringify({
                  Message:
                    summarizeJob?.summarize_job_name ||
                    createSummarizeJobName(patient.patient_id),
                  history_of_present_illness: patient.hpi || "",
                  interval_history: patient.interval_history || "",
                  assessment: patient.assessment || "",
                  plan: patient.plan || "",
                  interim_history: patient.interval_history || "",
                }),
              },
            ],
          };
        } else if (selectedFunction === "RunMacrosFunction") {
          // Construct RunMacros event structure
          eventStructure = {
            Records: [
              {
                body: JSON.stringify({
                  Message:
                    summarizeJob?.summarize_job_name ||
                    createSummarizeJobName(patient.patient_id),
                  patient_id: patient.patient_id,
                }),
              },
            ],
          };
        } else if (selectedFunction === "RunICD10CodingV2") {
          // Construct RunICD10CodingV2 event structure
          eventStructure = {
            Records: [
              {
                body: JSON.stringify({
                  Message:
                    summarizeJob?.summarize_job_name ||
                    createSummarizeJobName(patient.patient_id),
                }),
              },
            ],
          };
        } else if (selectedFunction === "RunChatGPTSummarizeFunction") {
          // Construct RunChatGPTSummarizeFunction event structure
          const transcribeJobName =
            transcribeJob?.transcribe_job_name ||
            createTranscribeJobName(patient.patient_id);
          eventStructure = {
            Records: [
              {
                body: JSON.stringify({
                  Type: "Notification",
                  Message: `Job:${transcribeJobName} has finished`,
                  MessageAttributes: {
                    transcribe_job_name: {
                      Type: "String",
                      Value: transcribeJobName,
                    },
                    patient_id: {
                      Type: "String",
                      Value: trimmedPatientId,
                    },
                  },
                }),
              },
            ],
          };
        } else if (selectedFunction === "RunUpdateChartHistoryFunction") {
          // Construct RunUpdateChartHistoryFunction event structure
          eventStructure = {
            Records: [
              {
                body: JSON.stringify({
                  appointment_id: patient?.appointment_id || "",
                  patient_id: trimmedPatientId,
                  token: patient?.doctor_access_token || "",
                }),
              },
            ],
          };
        } else if (selectedFunction === "RunInteguruSchedulePull") {
          // Construct RunInteguruSchedulePullAsync event structure
          const doctorName = patient?.doctor_name || "";
          eventStructure = {
            doctor: {
              id: patient?.doctor_npi || "",
              first_name: doctorName,
              last_name: doctorName,
              username: extractUsernameFromEmail(patient?.doctor_user_email),
              user_id: patient?.user_id || "",
              access_token: patient?.doctor_access_token || "",
            },
            date: formatDateMDYYYY(),
          };
        } else if (selectedFunction === "RunTranscriptionFunction") {
          const convId = conversationId || `${patient.patient_id}`;
          const audioLength = transcribeJob?.audio_length || "";
          transcriptionDataRef.current = { convId, audioLength };
          if (platform) {
            eventStructure = {
              Records: [
                {
                  body: JSON.stringify(
                    buildTranscriptionBody(convId, platform, audioLength),
                  ),
                },
              ],
            };
          } else {
            setEventJson("");
          }
        }

        if (eventStructure) {
          setEventJson(JSON.stringify(eventStructure, null, 2));
        }
      } catch (error) {
        console.error("Error auto-populating event JSON:", error);
        setError(`Failed to fetch patient data: ${error.message}`);
        setEventJson(""); // Clear the loading message
      } finally {
        setLoadingPatientData(false);
      }
    };

    autoPopulateEventJson();
  }, [patientId, selectedFunction]);

  // Reset platform when switching away from RunTranscriptionFunction
  useEffect(() => {
    if (selectedFunction !== "RunTranscriptionFunction") {
      setPlatform("");
    }
  }, [selectedFunction]);

  // Reset merge conversation selection when switching functions or patient
  useEffect(() => {
    setSelectedConversationId("");
    if (isMergeAudioChunksFunction) {
      setEventJson("");
    }
  }, [selectedFunction, patientId, isMergeAudioChunksFunction]);

  const handleSelectMergeConversation = (conversationId) => {
    setSelectedConversationId(conversationId);
    setEventJson(buildMergeAudioChunksEventJson(conversationId));
    setError(null);
  };

  // Rebuild RunTranscriptionFunction JSON when platform changes (updates bucket, object_key, platform field)
  useEffect(() => {
    if (selectedFunction !== "RunTranscriptionFunction" || !platform) return;

    const { convId, audioLength } = transcriptionDataRef.current;
    if (!convId) return; // Patient data not yet loaded

    const eventStructure = {
      Records: [
        {
          body: JSON.stringify(
            buildTranscriptionBody(convId, platform, audioLength),
          ),
        },
      ],
    };
    setEventJson(JSON.stringify(eventStructure, null, 2));
  }, [platform]); // eslint-disable-line react-hooks/exhaustive-deps

  // Format JSON
  const formatJson = () => {
    try {
      const parsed = JSON.parse(eventJson);
      setEventJson(JSON.stringify(parsed, null, 2));
    } catch (e) {
      // Already invalid, user will see the error
    }
  };

  // Copy response to clipboard
  const copyResponse = () => {
    navigator.clipboard.writeText(JSON.stringify(response, null, 2));
  };

  // Handle lambda invocation
  const handleInvoke = async () => {
    if (!selectedFunction) {
      setError("Please select a Lambda function");
      return;
    }

    if (
      selectedFunction === "RunTranscriptionFunction" &&
      !platform
    ) {
      setError("Please select a platform (Webapp or Mobile) for RunTranscriptionFunction");
      return;
    }

    if (!eventJson.trim()) {
      setError("Please provide event JSON");
      return;
    }

    if (!jsonValid) {
      setError("Please fix JSON syntax errors");
      return;
    }

    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      const parsedEvent = JSON.parse(eventJson);

      const result = await invokeLambda(
        selectedFunction,
        parsedEvent,
        "RequestResponse",
      );
      setResponse(result);
    } catch (err) {
      setError(`Invocation failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="bg-white rounded-lg border shadow-sm">
        <div className="p-6 border-b">
          <h1 className="text-2xl font-semibold text-gray-900">
            Lambda Invoker Dashboard
          </h1>
          <p className="text-gray-600 mt-1">
            Manually invoke AWS Lambda functions with custom event data
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Function Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Lambda Function
              </label>
              <div className="relative">
                <select
                  value={selectedFunction}
                  onChange={(e) => setSelectedFunction(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                >
                  <option value="">Choose a function...</option>
                  {availableFunctions.map((fn) => (
                    <option key={fn} value={fn}>
                      {fn}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Patient ID
              </label>
              <input
                type="text"
                value={patientId}
                onChange={(e) => setPatientId(e.target.value)}
                placeholder="Enter patient ID..."
                disabled={isUploadConversationFunction}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  isUploadConversationFunction
                    ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "border-gray-300"
                }`}
              />
              {isUploadConversationFunction && (
                <p className="mt-2 text-sm text-amber-600">
                  Use the audio lookup section below to create the event JSON to
                  use for this function.
                </p>
              )}
            </div>
          </div>

          {/* Platform Selection — RunTranscriptionFunction only */}
          {isRunTranscriptionFunction && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Platform{" "}
              </label>
              <div className="relative">
                <select
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white ${
                    !platform
                      ? "border-amber-400 bg-amber-50"
                      : "border-gray-300"
                  }`}
                >
                  <option value="">Select platform...</option>
                  <option value="web">Webapp</option>
                  <option value="mobile">Mobile</option>
                </select>
                <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
              {!platform && (
                <p className="mt-1 text-sm text-amber-600 flex items-center gap-1">
                  <AlertCircle className="h-3.5 w-3.5" />
                  Platform is required for RunTranscriptionFunction
                </p>
              )}
            </div>
          )}

          {isMergeAudioChunksFunction && patientId.trim() && (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                Conversation ID Finder
              </h3>
              <ConversationIdFinder
                patientId={patientId.trim()}
                onSelectConversation={handleSelectMergeConversation}
                actionLabel="Use conversation"
                selectedConversationId={selectedConversationId}
                description={`Select a conversation ID from ${environment.realtimeAudioChunksTable} to populate the MergeAudioChunksFunction event JSON.`}
                refreshLabel="Refresh conversations"
              />
            </div>
          )}

          {isMergeAudioChunksFunction && !patientId.trim() && (
            <p className="text-sm text-amber-600 flex items-center gap-1">
              <AlertCircle className="h-3.5 w-3.5" />
              Enter a patient ID to load available conversation IDs.
            </p>
          )}

          {/* Event JSON Input */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Event JSON
              </label>
              <button
                onClick={formatJson}
                disabled={!jsonValid}
                className="px-3 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Format JSON
              </button>
            </div>
            <div className="relative">
              <textarea
                value={eventJson}
                onChange={(e) => setEventJson(e.target.value)}
                placeholder="Enter your event JSON here..."
                rows={12}
                disabled={loadingPatientData}
                className={`w-full px-3 py-2 border rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical ${
                  !jsonValid ? "border-red-300 bg-red-50" : "border-gray-300"
                } ${loadingPatientData ? "opacity-60 cursor-not-allowed" : ""}`}
              />
              {loadingPatientData && (
                <div className="absolute top-2 right-2 flex items-center gap-2">
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
                  <span className="text-sm text-blue-600">
                    Loading patient data...
                  </span>
                </div>
              )}
              {!jsonValid && (
                <div className="absolute top-2 right-2">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                </div>
              )}
            </div>
            {!jsonValid && (
              <p className="mt-1 text-sm text-red-500">Invalid JSON format</p>
            )}
            <p className="mt-2 text-sm text-amber-600 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Make sure to verify JSON before submitting, some values may not be
              correct.
            </p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-2" />
                <div>
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Invoke Button */}
          <div className="flex justify-center">
            <button
              onClick={handleInvoke}
              disabled={
                loading ||
                !jsonValid ||
                !selectedFunction ||
                (isRunTranscriptionFunction && !platform)
              }
              className="px-3 py-2 bg-purple-600 text-white rounded hover:purple-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                  Invoking...
                </>
              ) : (
                <>Trigger</>
              )}
            </button>
          </div>

          {/* Response Display */}
          {response && (
            <div className="border border-gray-200 rounded-lg p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <LambdaRunBadge
                    status={runStatus}
                    durationMs={runDurationMs}
                  />
                  <span className="text-sm text-gray-600">
                    HTTP {response.statusCode} · {response.functionName}
                  </span>
                </div>
                <button
                  onClick={copyResponse}
                  className="shrink-0 text-sm text-gray-600 hover:text-gray-900 flex items-center"
                >
                  <Copy className="h-4 w-4 mr-1" />
                  Copy
                </button>
              </div>

              {response.functionError && (
                <p className="text-sm text-red-700">
                  The function raised an error ({response.functionError}). See
                  the logs below.
                </p>
              )}

              <LambdaLogPanel
                lines={trackedRun?.lines || []}
                status={trackedRun?.status || runStatus}
                finished={trackedRun?.finished}
                unavailable={trackedRun?.unavailable}
                error={trackedRun?.error}
                onRefresh={refreshRunLogs}
                refreshing={refreshingRunLogs}
                requestId={response.requestId}
                functionName={response.functionName}
                heightClass="max-h-96"
              />

              <details className="bg-gray-50 rounded border">
                <summary className="cursor-pointer px-3 py-2 text-sm text-gray-700">
                  Raw invocation response
                </summary>
                <pre className="text-xs text-gray-800 overflow-auto px-3 pb-3">
                  {JSON.stringify(response, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </div>
      </div>

      <AudioLookup />
    </div>
  );
};

export default LambdaInvokerDashboard;
